import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonService } from './../../../services/common/common.service';
import { LolLoggerService } from '../../../common/shared/lol-logger.service';
import { CppService } from '../../../services/cpp/cpp.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '../../../services/infrastructure/local-storage.service';
import { MasterConstants } from '../../constants/master-constants';

const REQUEST_TYPE_CPP = 'CPP';
const COMMAND = {
  PLACE_ORDER: 'PlaceOrder'

} as const;
const orderHandoffMessage = 'Your items have been sent to the CPP Order screen. Please review and place your order there. To create another order, click the Home icon to refresh the chat.';

@Component({
  selector: 'app-ai-cpp-chatbot',
  templateUrl: './ai-cpp-chatbot.component.html',
  styleUrls: ['./ai-cpp-chatbot.component.scss']
})
export class AiCppChatbotComponent implements OnInit, OnDestroy {
  @Input() userDisplayName = '';
  @Output() orderCompleted = new EventEmitter<void>();
  @Output() navigateToTracking = new EventEmitter<void>();
  @Output() cartChanged = new EventEmitter<string[]>();
  @Output() closeChatWindow = new EventEmitter<void>();
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  iconBase = 'assets/icon/ai-chatbot-icons';
  cppConfig: any = null;
  messages: any[] = [];
  userInput = '';
  isTyping = false;
  processingTimestamp = new Date();
  subscription = new Subscription();
  processingMessage: any;
  welcomeMessage: any;
  initialVisibleProducts: number = 5;
  awaitingSelection = false;
  selectedProduct: any = null;
  selectedQuantity: number | null = null;
  selectedOrderItems: Array<{ product: any; quantity: number }> = [];
  virtualAssitant = 'Evolve';
  cartItemKeys: string[] = [];
  cartItemNumbers: string[] = [];
  defaultCartQuantity = 1;
  orderHandedOff = false;
  private cartSummaryMessage: any = null;
  multiAccountSelection = true;
  initialVisibleAccounts = 5;
  selectedAccount: any = null;
  accountsLocked = false;
  private accountSelectionRequired = false;
  isCPPPriceAccess = false;
  selectedAccountNumber: number;


  constructor(private router: Router, private commonService: CommonService, private cppService: CppService,
    private localStorageService: LocalStorageService, private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {

    this.isCPPPriceAccess = this.commonService.checkUserRoleAccessType(
      MasterConstants.FUNCTION_CODE.CROP_PROTECTION_PRODUCT_PRICE_LIST,
      MasterConstants.USER_ACCESS.EDIT_FULL);

    this.subscription.add(this.commonService.getUserSelectedAccountForChat().subscribe((result: any) => {

      let userAccounts = result;
      this.accountSelectionRequired = this.multiAccountSelection && Array.isArray(userAccounts) && userAccounts.length > 1;

      if (userAccounts.length == 1) {
        this.selectedAccountNumber = userAccounts[0].accountNumber;
        this.welcomeMessage = "Great choice! I'll help you look for product availability and create a CPP order. You can search by Product Name, Active Ingredient, or Item Number (for example, \"Sterling Blue\", \"glyphosate\", or \"1749064\")";
        this.processingMessage = "Your request is being processed...";
        this.messages = [{
          type: 'welcome',
          text: this.welcomeMessage,
          timestamp: new Date()
        }];
      }
      else {
        this.welcomeMessage = "Great choice! I'll help you look for product availability and create a CPP order. You can search by Product Name, Active Ingredient, or Item Number (for example, \"Sterling Blue\", \"glyphosate\", or \"1749064\"). Please select a ship to location from list given below.";
        this.processingMessage = "Your request is being processed...";
        const welcomeChatResponse: any = {
          type: 'welcome',
          text: this.welcomeMessage,
          timestamp: new Date()
        };

        if (this.multiAccountSelection && Array.isArray(userAccounts) && userAccounts.length > 1) {
          const accounts = [...userAccounts];
          welcomeChatResponse.accounts = accounts;
          welcomeChatResponse.filteredAccounts = accounts;
          welcomeChatResponse.accountSearch = '';
          welcomeChatResponse.visibleAccounts = accounts.slice(0, this.initialVisibleAccounts);
          welcomeChatResponse.showViewMoreAccounts = accounts.length > this.initialVisibleAccounts;
        }

        this.messages = [welcomeChatResponse];
      }

    }, error => {
      LolLoggerService.error(error);
      this.isTyping = false;
    }));

    this.cppConfig = {
      "inputPlaceholder": "Describe your order... (e.g. I want to order X1 10 qty)",
      "followUpInputPlaceholder": "Type your message...",
      "processingMessage": this.processingMessage,
      "typingDelayMs": 1500
    }
  }

  get inputPlaceholder(): string {
    if (this.isTyping) {
      return this.processingMessage;
    }

    const hasUserMessage = this.messages.some(m => m.type === 'user');
    return hasUserMessage ? this.cppConfig.followUpInputPlaceholder : this.cppConfig.inputPlaceholder;
  }

  get isConfirmPromptPending(): boolean {
    return this.messages?.some((item: any) => item.type === 'confirmation-prompt') ?? false;
  }

  get isAccountSelectionPending(): boolean {
    return this.accountSelectionRequired && !this.selectedAccount;
  }

  askAI(message: string): void {
    const searchedText = message.trim();
    if (searchedText && this.accountSelectionRequired) {
      this.accountsLocked = true;
    }

    const request = { userMessage: searchedText, requestType: REQUEST_TYPE_CPP, accountNumber: this.selectedAccountNumber };
    this.subscription.add(this.commonService.getAIResponse(request).subscribe((result: any) => {
      const responseMessage = result?.message?.responseData;
      const matchedProducts: any[] = Array.isArray(responseMessage) ? responseMessage : [];
      // The AI only echoes a search term when it recognised one; blank means the
      // input was not a product term, so the message is shown without it.
      const searchTerm = this.normaliseSearchTerm(result?.message?.aiMessage);

      setTimeout(() => {
        if ((!matchedProducts.length || result == null) && searchTerm.length < 60) {
          this.awaitingSelection = false;
          this.messages.push({
            type: 'no-results',
            searchTerm,
            timestamp: new Date()
          });
        }
        else if ((!matchedProducts.length || result == null) && searchTerm.length > 80) {
          this.awaitingSelection = false;
          this.messages.push({
            type: 'exceptionMessage',
            searchTerm,
            timestamp: new Date()
          });
        }
        else {
          this.awaitingSelection = true;
          this.selectedProduct = null;
          this.selectedQuantity = null;
          const allProducts = [...matchedProducts];
          this.messages.push({
            type: 'product-list',
            searchTerm,
            timestamp: new Date(),
            resultCount: matchedProducts.length,
            products: allProducts,
            visibleProducts: allProducts.slice(0, this.initialVisibleProducts),
            showViewMore: allProducts.length > this.initialVisibleProducts,
          });
          this.syncCartState();
        }

        this.isTyping = false;
        this.scrollToBottom();
      }, 1500);
    }, error => {
      LolLoggerService.error(error);
      this.isTyping = false;
    }));
  }

  sendMessage(): void {
    const text = this.userInput.trim();
    if (!text || this.isTyping || this.isAccountSelectionPending || this.orderHandedOff || !this.cppConfig) {
      return;
    }

    const userMessage = { type: 'user', text, timestamp: new Date(), readTickState: 'grey' };
    this.messages = this.messages.filter((item: any) => item.type !== 'confirmation-prompt');
    this.messages.push(userMessage);
    setTimeout(() => {
      userMessage.readTickState = 'yellow';
    }, 1000);

    this.userInput = '';
    this.processingTimestamp = new Date();
    this.scrollToBottom();

    // Selection mode takes precedence so "1" is never interpreted as quantity.
    if (this.awaitingSelection) {
      this.sendSelection(text);
      return;
    }

    this.isTyping = true;
    this.askAI(text);
  }

  sendSelection(selectionValue: string, message?: any): void {
    if (!selectionValue) {
      return;
    }

    if (this.tryHandleActionSelection(selectionValue, message)) {
      return;
    }

    // Free text is not an action, so treat it as a new product search.
    this.awaitingSelection = false;
    this.isTyping = true;
    this.askAI(selectionValue);
  }

  private tryHandleActionSelection(selectionValue: string, message?: any): boolean {
    const normalized = selectionValue.trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    if (normalized === 'continue to order details') {
      if (message?.orderConfirmed || this.isTyping || this.orderHandedOff) {
        return true;
      }

      this.isTyping = true;
      const requestData = {
        userMessage: COMMAND.PLACE_ORDER, requestType: REQUEST_TYPE_CPP,
        requestData: this.cartItemNumbers, accountNumber: this.selectedAccountNumber
      };

      this.subscription.add(this.commonService.getAIResponse(requestData).subscribe((result: any) => {
        this.isTyping = false;
        if (result?.message?.aiMessage === 'RedirectToCPP') {
          this.localStorageService.set('tempCartData', JSON.stringify({ accountNo: result?.message?.responseData, itemNumbers: this.cartItemNumbers }));
          this.orderHandedOff = true;
          this.awaitingSelection = false;
          this.userInput = '';
          this.messages.push({
            type: 'ai-text',
            text: orderHandoffMessage,
            timestamp: new Date(),
            orderHandoffMessage: true
          });
          // Render and scroll while the chat is still visible, so the message is in view on reopen.
          this.changeDetectorRef.detectChanges();
          this.scrollToBottom(false);
          this.closeChatWindow.emit();
          this.cppService.setCppOrderId(0);
          // Force the target component to reload even when we are already on that route.
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate(['/cpp/cpp-order-create']);
          });
        }
      }, error => {
        this.isTyping = false;
        this.messages.push({
          type: 'ai-text',
          text: 'Failed to continue to order. Please try again.',
          timestamp: new Date()
        });
        this.scrollToBottom();
        LolLoggerService.error(error);
      }));
      return true;
    }

    if (normalized === 'go to home') {
      this.resetForNewCppOrder();
      this.orderCompleted.emit();
      return true;
    }

    if (normalized === 'track shipment') {
      if (this.isConfirmPromptPending) {
        return true;
      }
      this.showConfirmationPrompt('Are you sure you want to discard this order?', 'confirm-track-shipment');
      return true;
    }

    if (normalized === 'cancel-track-shipment') {
      if (message) {
        this.messages = this.messages.filter(item => item !== message);
      }
      return true;
    }

    if (normalized === 'confirm-track-shipment') {
      this.resetForNewCppOrder();
      this.navigateToTracking.emit();
      return true;
    }

    return false;
  }

  /**
   * Strips wrapping quotes from the search term so the template can apply its own
   * quotation marks without ending up with doubled quotes.
   */
  private normaliseSearchTerm(value: any): string {
    let term = (value ?? '').toString().trim();

    // The AI may return the term already wrapped in straight or smart quotes.
    while (term.length > 1 && /^["'\u2018\u201c].*["'\u2019\u201d]$/.test(term)) {
      term = term.slice(1, -1).trim();
    }

    return term;
  }

  private showConfirmationPrompt(text: string, confirmAction: string): void {
    this.messages.push({
      type: 'confirmation-prompt',
      text,
      confirmAction,
      timestamp: new Date()
    });
    this.scrollToBottom();
  }

  private resetForNewCppOrder(): void {
    // Clear the conversation so a fresh CPP chat starts next time the tile is picked.
    this.messages = [];
    this.userInput = '';
    this.awaitingSelection = false;
    this.orderHandedOff = false;
    this.cartItemKeys = [];
    this.cartItemNumbers = [];
    this.cartSummaryMessage = null;
    this.cartChanged.emit([]);
    this.selectedProduct = null;
    this.selectedQuantity = null;
    this.selectedOrderItems = [];
    this.selectedAccount = null;
    this.accountsLocked = false;
  }

  private queueCurrentSelection(): void {
    if (!this.selectedProduct || !this.selectedQuantity || this.selectedQuantity <= 0) {
      return;
    }

    const currentKey = this.getProductKey(this.selectedProduct);
    const existingIndex = this.selectedOrderItems.findIndex(entry => this.getProductKey(entry.product) === currentKey);

    if (existingIndex >= 0) {
      this.selectedOrderItems[existingIndex].quantity = this.selectedQuantity;
      this.selectedOrderItems[existingIndex].product = this.selectedProduct;
    } else {
      this.selectedOrderItems.push({
        product: this.selectedProduct,
        quantity: this.selectedQuantity
      });
    }
  }

  private getProductKey(product: any): string {
    return (product?.itemNumber || product?.ItemNumber || product?.itemId || product?.ItemId || '').toString().toLowerCase();
  }

  get cartCount(): number {
    return this.cartItemKeys.length;
  }

  /** Item numbers of every product the user currently has selected (added to cart). */
  get selectedItemNumbers(): string[] {
    return this.selectedOrderItems
      .map(entry => (entry.product?.itemNumber || entry.product?.ItemNumber).toString())
      .filter(itemNumber => !!itemNumber);
  }

  isInCart(product: any): boolean {
    return this.cartItemKeys.includes(this.getProductKey(product));
  }

  toggleCart(product: any, event?: Event): void {
    event?.stopPropagation();

    if (!product || this.isTyping || this.orderHandedOff) {
      return;
    }

    const key = this.getProductKey(product);
    if (!key) {
      return;
    }

    if (this.isInCart(product)) {
      this.cartItemKeys = this.cartItemKeys.filter(cartKey => cartKey !== key);
      this.selectedOrderItems = this.selectedOrderItems.filter(entry => this.getProductKey(entry.product) !== key);

      if (this.getProductKey(this.selectedProduct) === key) {
        this.selectedProduct = null;
        this.selectedQuantity = null;
      }
    } else {
      this.selectedProduct = product;
      this.selectedQuantity = this.defaultCartQuantity;
      this.awaitingSelection = false;
      this.queueCurrentSelection();
      this.cartItemKeys = [...this.cartItemKeys, key];
    }

    this.syncCartState();
    this.scrollToBottom();
  }

  /** Keeps cart-derived state in sync. The summary card is created */
  private syncCartState(): void {
    this.cartItemNumbers = this.selectedItemNumbers;
    this.cartChanged.emit(this.cartItemNumbers);

    if (!this.selectedOrderItems.length) {
      this.messages = this.messages.filter(message => message !== this.cartSummaryMessage);
      this.cartSummaryMessage = null;
      return;
    }

    // Re-append so the summary + footer links stay below the newest product list.
    if (this.cartSummaryMessage) {
      this.messages = this.messages.filter(message => message !== this.cartSummaryMessage);
    }

    this.cartSummaryMessage = { type: 'cart-summary', timestamp: new Date() };
    this.messages.push(this.cartSummaryMessage);
  }

  getAccountDisplayName(account: any): string {
    const name = (account?.accountName ?? '').trim();
    const city = (account?.city ?? '').trim();
    const accountName = city && name.toLowerCase().endsWith(city.toLowerCase())
      && (name.length === city.length || /[\s,-]/.test(name.charAt(name.length - city.length - 1)))
      ? name.slice(0, -city.length).replace(/[\s,-]+$/, '') : name;
    return [accountName, city].filter(Boolean).join(' - ');
  }

  isAccountSelected(account: any): boolean {
    return !!this.selectedAccount && this.selectedAccount.accountNumber === account?.accountNumber;
  }

  toggleAccount(account: any): void {
    if (!account || this.isTyping || this.orderHandedOff || this.accountsLocked) {
      return;
    }

    this.selectedAccount = this.isAccountSelected(account) ? null : account;
    this.selectedAccountNumber = this.selectedAccount?.accountNumber;
    // Drop any draft text so the search always starts fresh against the newly picked ship to location.
    this.userInput = '';
    this.messages = this.messages.filter(message => !message.isAccountSelectionPrompt);
    if (this.selectedAccount) {
      this.messages.push({
        type: 'ai-text',
        text: 'Please describe what you need or ask me to search for a product',
        timestamp: new Date(),
        isAccountSelectionPrompt: true
      });
    }
    this.scrollToBottom();
  }

  filterAccounts(message: any, searchText: string): void {
    const searchTerm = searchText.trim().toLowerCase();
    message.filteredAccounts = message.accounts.filter((account: any) =>
      [account.accountName, account.accountNumber, account.address1, account.city, account.state, account.zipCode]
        .filter(value => value != null)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm));
    message.visibleAccounts = message.filteredAccounts.slice(0, this.initialVisibleAccounts);
    message.showViewMoreAccounts = message.filteredAccounts.length > this.initialVisibleAccounts;
  }

  viewMoreAccounts(message: any): void {
    const visibleCount = message.visibleAccounts.length + this.initialVisibleAccounts;
    message.visibleAccounts = message.filteredAccounts.slice(0, visibleCount);
    message.showViewMoreAccounts = message.visibleAccounts.length < message.filteredAccounts.length;
  }

  viewMore(message: any): void {
    message.visibleProducts = message.products;
    message.showViewMore = false;
    this.scrollToBottom();
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private scrollToBottom(deferred = true): void {
    const scroll = () => {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    };

    deferred ? setTimeout(scroll) : scroll();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
